import { useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type AvailableDate } from '@/api';

interface CalendarViewProps {
  availability: AvailableDate[];
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  from: string;
  to: string;
}

export function CalendarView({
  availability,
  selectedDate,
  onSelectDate,
  from,
  to,
}: CalendarViewProps) {
  const [currentMonth, setCurrentMonth] = useState(() => {
    const date = new Date(from);
    return { year: date.getFullYear(), month: date.getMonth() };
  });

  const availabilityMap = new Map(availability.map((a) => [a.date, a]));

  const daysInMonth = new Date(currentMonth.year, currentMonth.month + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentMonth.year, currentMonth.month, 1).getDay();

  const monthNames = [
    'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
    'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
  ];

  const dayNames = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

  const prevMonth = () => {
    setCurrentMonth((prev) => {
      if (prev.month === 0) {
        return { year: prev.year - 1, month: 11 };
      }
      return { year: prev.year, month: prev.month - 1 };
    });
  };

  const nextMonth = () => {
    setCurrentMonth((prev) => {
      if (prev.month === 11) {
        return { year: prev.year + 1, month: 0 };
      }
      return { year: prev.year, month: prev.month + 1 };
    });
  };

  const formatDateString = (day: number): string => {
    const month = String(currentMonth.month + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${currentMonth.year}-${month}-${dayStr}`;
  };

  const isDateInRange = (dateStr: string): boolean => {
    return dateStr >= from && dateStr <= to;
  };

  const isToday = (dateStr: string): boolean => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr === today;
  };

  const renderDays = () => {
    const days = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <div
          key={`empty-${i}`}
          className="aspect-square w-full max-w-[42px] min-w-0 mx-auto"
        />
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = formatDateString(day);
      const dateInfo = availabilityMap.get(dateStr);
      const inRange = isDateInRange(dateStr);
      const isSelected = dateStr === selectedDate;
      const today = isToday(dateStr);
      const isAvailable = inRange && !!dateInfo?.available;

      let cellStateClass = '';
      if (!isAvailable) {
        cellStateClass = 'text-muted-foreground/35 cursor-not-allowed hover:bg-transparent';
      } else if (isSelected) {
        cellStateClass = 'bg-primary text-primary-foreground font-bold shadow-md shadow-primary/25 cursor-pointer scale-[1.04]';
      } else if (today) {
        cellStateClass = 'border-2 border-primary text-primary font-bold cursor-pointer hover:bg-primary/10 hover:text-primary';
      } else {
        cellStateClass = 'text-foreground font-medium hover:bg-primary/10 hover:text-primary cursor-pointer transition-transform hover:scale-[1.02]';
      }

      days.push(
        <button
          key={day}
          type="button"
          onClick={() => isAvailable && onSelectDate(dateStr)}
          disabled={!isAvailable}
          className={`aspect-square w-full max-w-[42px] min-w-0 mx-auto flex items-center justify-center rounded-xl relative text-sm transition-all duration-150 select-none ${cellStateClass}`}
        >
          <span>{day}</span>
          {isAvailable && (dateInfo?.slotsAvailable ?? 0) > 0 && !isSelected && (
            <span className="absolute bottom-1 w-1.5 h-1.5 rounded-full bg-primary/80" />
          )}
        </button>
      );
    }

    return days;
  };

  return (
    <div className="bg-card text-card-foreground rounded-2xl border border-border p-4 sm:p-5 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={prevMonth}
          className="p-2 hover:bg-muted text-muted-foreground hover:text-foreground rounded-xl transition-colors cursor-pointer"
          aria-label="Mês anterior"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <h3 className="text-base font-bold text-foreground">
          {monthNames[currentMonth.month]} {currentMonth.year}
        </h3>
        <button
          type="button"
          onClick={nextMonth}
          className="p-2 hover:bg-muted text-muted-foreground hover:text-foreground rounded-xl transition-colors cursor-pointer"
          aria-label="Próximo mês"
        >
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {dayNames.map((name) => (
          <div
            key={name}
            className="aspect-square w-full max-w-[42px] min-w-0 mx-auto flex items-center justify-center text-xs font-semibold text-muted-foreground/80 uppercase tracking-wider"
          >
            {name}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">
        {renderDays()}
      </div>

      <div className="mt-4 pt-3 border-t border-border flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-primary" />
          <span>Selecionado</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full border-2 border-primary" />
          <span>Hoje</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full bg-muted-foreground/30" />
          <span>Indisponível</span>
        </div>
      </div>
    </div>
  );
}
